import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ADD_PAYMENT } from 'store/mutations';
import { useMutation } from '@apollo/client';
import usePayIfLoyaltyAndConfirmBookingMutation from 'components/mutations/usePayIfLoyaltyAndConfirmBookingMutation';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { historyShape, bookingShape } from 'constants/propTypes';
import { getBookingJourneysURL } from 'utils/urls';
import { isVoucherPayment, isSubscriptionOnly } from 'utils/payment';
import { productFamilyCodes } from 'types/productFamilyCodes';
import LoyaltyLoggedOutModal from 'components/shared/LoyaltyLoggedOutModal';

const PayAndConfirmMutation = ({ bookingReference, redirectUrl, subscriptionUrl, paymentUrlPrefix, history, children, booking }) => {
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isMissedDeparture = new URLSearchParams(history.location.search).get('isMissedDeparture') === 'true';
  const isFlowForwardCapped = new URLSearchParams(history.location.search).get('isFlowForwardCapped') === 'true';
  const shouldWaivePoints = new URLSearchParams(history?.location?.search).get('waivePoints') === 'true';

  const isSubscriptionOnlyBooking = isSubscriptionOnly(booking);
  const [hasLoyaltyAccountError, setLoyaltyAccountError] = useState(false);
  const [confirmBooking, { loading: confirmBookingLoading, error: confirmBookingError }] = usePayIfLoyaltyAndConfirmBookingMutation();
  const [addPayment, { loading: addPaymentLoading, error: addPaymentError }] = useMutation(ADD_PAYMENT);

  const loading = addPaymentLoading || confirmBookingLoading;
  const error = addPaymentError || confirmBookingError;
  const isLoyaltyLoggedOut =
    booking.productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) && !membershipNumber;
  const handleCloseModal = () => setLoyaltyAccountError(!hasLoyaltyAccountError);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}${redirectUrl}`
    });
  }

  const payAndConfirm = async ({ variables }) => {
    if (isLoyaltyLoggedOut) {
      const hasProvisionalExtras =
        (booking.outboundExtras || []).concat(booking.inboundExtras || []).filter((e) => !e.cancelled && e.provisional).length > 0;

      if (!hasProvisionalExtras) {
        setLoyaltyAccountError(true);

        return;
      }
    }

    const urlSegmentOne = paymentUrlPrefix.split('/')[1];

    const paymentsToAdd = variables.payments.filter((payment) => !isVoucherPayment(payment));
    if (paymentsToAdd.length) {
      await addPayment({
        variables: {
          payments: paymentsToAdd,
          bookingReference: variables.bookingReference,
          reason: variables.reason
        }
      });
    }

    await confirmBooking({
      variables: {
        bookingData: booking,
        membershipNumber,
        isExchange: ['exchange'].includes(urlSegmentOne),
        isMissedDeparture,
        isFlowForwardCapped,
        shouldWaivePoints,
        sendConfirmationEmail:
          urlSegmentOne &&
          ['booking', 'exchange'].includes(urlSegmentOne) &&
          !booking.isGroupBooking &&
          !history.location.search.includes('cancel-voucher') &&
          !isSubscriptionOnlyBooking
      }
    });

    if (subscriptionUrl) {
      history.push(subscriptionUrl, {
        shouldRetry: true
      });

      return;
    }

    const bookingJourneysURL = getBookingJourneysURL(bookingReference, membershipNumber);
    history.push(bookingJourneysURL);
  };

  return (
    <>
      <LoyaltyLoggedOutModal isOpen={hasLoyaltyAccountError} onClose={handleCloseModal} />
      {children(payAndConfirm, { loading })}
    </>
  );
};

PayAndConfirmMutation.defaultProps = {
  redirectUrl: ''
};

PayAndConfirmMutation.propTypes = {
  bookingReference: PropTypes.string,
  redirectUrl: PropTypes.string,
  subscriptionUrl: PropTypes.string,
  paymentUrlPrefix: PropTypes.string,
  children: PropTypes.func,
  history: historyShape,
  booking: bookingShape
};

export default PayAndConfirmMutation;
